import { useState, useCallback, useMemo } from 'react';

import { Site } from '../types/site.types';
import { useGetProperties, GetPropertiesParams } from '../api';
import { transformPropertiesToSites } from '../utils/transform';
import { propertiesService } from '../services/properties.service';
import { usePropertySearchStore } from '../stores/property-search.store';

import { useLoadingProgress } from '@/hooks/use-loading-progress';

export const useProperties = () => {
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const { setIsLoading } = useLoadingProgress();

  // Get state from global store
  const {
    searchInputValues,
    committedSearchParams,
    currentPage,
    itemsPerPage,
    sortField: storeSortField,
    sortOrder,
    setSearchInputValues,
    setCommittedSearchParams,
    setCurrentPage,
    setItemsPerPage,
    setSortField: setStoreSortField,
    setSortOrder,
    clearSearch,
  } = usePropertySearchStore();

  // Convert store sortField to proper type
  const sortField = useMemo<
    | 'updatedAt'
    | 'nameAbbreviation'
    | 'constructionCompanyName'
    | 'salesRepresentativeName'
    | undefined
  >(() => {
    if (!storeSortField) return 'updatedAt';
    return storeSortField as
      | 'updatedAt'
      | 'nameAbbreviation'
      | 'constructionCompanyName'
      | 'salesRepresentativeName'
      | undefined;
  }, [storeSortField]);

  // Build API query parameters from committed search params only
  // Use useMemo to ensure React Query detects changes correctly
  const apiParams: GetPropertiesParams = useMemo(
    () => ({
      name: committedSearchParams.siteName,
      constructionCompanyName: committedSearchParams.constructionCompany,
      customerName: committedSearchParams.client,
      // All fields now use text values (label) directly for search
      salesRepresentativeName: committedSearchParams.salesRepresentative,
      departmentName: committedSearchParams.department,
      deliveryPersonName: committedSearchParams.delivery,
      // Send prefecture name directly instead of code
      prefectureName: committedSearchParams.prefecture || undefined,
      constructionPeriodFrom: committedSearchParams.constructionStartDate,
      constructionPeriodTo: committedSearchParams.constructionEndDate,
      // Only send hasArchived if explicitly true (to include archived items)
      // If not sent or false, backend will filter out archived items
      hasArchived: committedSearchParams.includeArchive === true ? true : undefined,
      page: currentPage,
      perPage: itemsPerPage,
      sort: sortField,
      orderBy: sortOrder,
    }),
    [committedSearchParams, currentPage, itemsPerPage, sortField, sortOrder]
  );
  // Handle sort change
  const handleSortChange = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      setStoreSortField(field);
      setSortOrder(order);
    },
    [setStoreSortField, setSortOrder]
  );

  // Fetch properties using React Query (only triggers when apiParams change)
  // Use isFetching instead of isLoading to show loading indicator while keeping old data
  const { data: apiResponse, isFetching, isError, refetch } = useGetProperties(apiParams);

  // Transform API data to Site format
  const sites = useMemo<Site[]>(() => {
    if (!apiResponse?.items) {
      return [];
    }
    const transformedSites = transformPropertiesToSites(apiResponse.items);
    return transformedSites;
  }, [apiResponse, currentPage]);

  // Total items from API pagination
  const totalItems = apiResponse?.pagination?.total || 0;

  // Sites are already filtered by the API, no need for client-side filtering
  const filteredSites = sites;

  // Handle search - Only called when user clicks search button

  const handleSearch = useCallback(
    (params: Record<string, unknown>, preservePage = false) => {
      setCommittedSearchParams(params);
      setSearchInputValues(params);
      // Only reset to page 1 if it's a new search, preserve page if restoring
      if (!preservePage) {
        setCurrentPage(1);
      }
      // Note: React Query will automatically refetch when apiParams change (which includes committedSearchParams and currentPage)
    },
    [setCommittedSearchParams, setSearchInputValues, setCurrentPage]
  );

  // Handle search change - Only updates form state, does NOT trigger API

  const handleSearchChange = useCallback(
    (params: Record<string, unknown>) => {
      setSearchInputValues(params);
    },
    [setSearchInputValues]
  );

  // Handle clear search, just reset the form state, do not reset the data in the table as required by the business
  const handleClear = useCallback(() => {
    clearSearch();
    setSelectedSites([]);
  }, [clearSearch]);

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  // Handle items per page change
  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: number) => {
      setItemsPerPage(itemsPerPage);
      setCurrentPage(1); // Reset to first page
    },
    [setItemsPerPage, setCurrentPage]
  );

  // Handle selection change
  const handleSelectionChange = useCallback((selectedIds: string[]) => {
    setSelectedSites(selectedIds);
  }, []);

  // Handle archive
  const handleArchive = useCallback(
    async (siteIds: string[]) => {
      if (siteIds.length === 0) {
        return;
      }
      try {
        setIsLoading(true);
        await propertiesService.archiveProperty(siteIds);
        // Refetch data after archiving
        await refetch();
        setSelectedSites([]);
      } catch (error) {
        console.error('Error archiving property:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [refetch]
  );

  // Handle restore
  const handleRestore = useCallback(
    async (siteIds: string[]) => {
      if (siteIds.length === 0) {
        return;
      }
      try {
        setIsLoading(true);
        await propertiesService.restoreProperty(siteIds);
        // Refetch data after restoring
        await refetch();
        setSelectedSites([]);
      } catch (error) {
        console.error('Error restoring property:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [refetch]
  );

  // Handle register autocomplete clear
  const handleRegisterAutocompleteClear = useCallback((_clearFn: () => void) => {
    // Store the clear function for later use
    // Don't call it immediately to avoid setState during render
  }, []);

  return {
    // Data
    searchResults: filteredSites,
    loading: isFetching, // Use isFetching to show loading while keeping old data
    error: isError,
    currentPage,
    itemsPerPage,
    totalItems,
    selectedSites,
    searchParams: searchInputValues, // Return input values for form fields
    committedSearchParams, // Also expose committed params if needed
    sortField: sortField,
    sortOrder,
    handleSortChange,

    // Actions
    handleSearch,
    handleSearchChange,
    handleClear,
    handlePageChange,
    handleItemsPerPageChange,
    handleSelectionChange,
    handleArchive,
    handleRestore,
    handleRegisterAutocompleteClear,
    refetch,
  };
};
